import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import DialogList from 'src/dialogs';

interface INachaBatch {
    nmId: number;
    periodNo: number;
    bankNo: number;
    compNo: number;
    createDate: string;
    depDate: string;
    nachaType: string;
    tsdNo?: number;
}

interface INachaEntry {
    ndId: number;
    tranCode: string;
    tsdNo?: number;
    tsdName: string;
    tranAmt: number;
    routing: number;
    bankAcct: string;
}

interface INachaHeaderRecord {
    priorityCode: string; imDestination: string; imId: string; createDate: string; createTime: string;
    fileId: string; recordSize: string; blockFactor: string; formatCode: string; destName: string;
    originName: string; refCode: string;
}

interface INachaBatchRecord {
    classCode: string; company: string; companyData: string; imId: string; entryClass: string;
    entryDesc: string; descDate: string; effDate: string; settleDate: string; statusCode: string;
    dfiId: string; batchNo: string;
}

interface INachaBControlRecord {
    serviceCode: string; addenda: string; entryHash: string; debit: string; credit: string;
    compId: string; dfiId: string; traceNo: string;
}

interface INachaFControlRecord {
    batchCount: string; blockCount: string; entryCount: string; entryHash: string; debit: string; credit: string;
}

const NACHA_TYPE_OPTIONS = [
    { value: 'A', label: 'Vendor Pay Generated Values' },
    { value: 'P', label: 'Penny Values / Vendor Generated' },
    { value: 'R', label: 'Penny Values / Vendor Pay Generated' },
    { value: 'Y', label: 'Zero Values / Vendor Generated' },
    { value: 'Z', label: 'Zero Values / Vendor Pay Generated' },
];

const fieldRowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' };
const fieldLabelStyle: React.CSSProperties = { fontWeight: 'bold', marginRight: '0.5rem' };

const FieldRow = (props: { label: string; value?: string }) => (
    <div style={fieldRowStyle}>
        <span style={fieldLabelStyle}>{props.label}</span>
        <span>{props.value}</span>
    </div>
);

const sectionHeadingStyle: React.CSSProperties = {
    width: '100%', textAlign: 'center', fontWeight: 'bold', border: '1px solid #ccc',
    borderRadius: '4px', padding: '0.4rem', marginBottom: '0.75rem', background: '#f0f0f0',
};

const SectionHeading = (props: { text: string }) => <div style={sectionHeadingStyle}>{props.text}</div>;

interface INachaDialogsProps {
    showDialog: (dialog: DialogList) => void;
    hideDialog: () => void;
}

// Ports the NACHA Direct Deposit flow (tsdcheck.fmt: DD_Parms -> Deposit/Nacha_Control ->
// Header/Batch + Control Data drill-downs -> Nacha file build). All four dialogs below are
// rendered as siblings (never nested inside one another's JSX) and every dialog switch is a
// single showDialog(...) call, per this app's Dialog launch rules (see feedback memory) -
// never hideDialog() immediately followed by showDialog() in the same handler.
const NachaDialogs = (props: INachaDialogsProps) => {
    const { register, getValues, setValue, errors, handleSubmit } = useForm();
    const { get: getLookups } = useFetch();
    const { get: getPeriodDate } = useFetch();
    const { get, post, del, response } = useFetch();

    const [banks, setBanks] = useState<Array<{ value: string; label: string }>>([]);
    const [companies, setCompanies] = useState<Array<{ value: string; label: string }>>([]);
    const [batches, setBatches] = useState<INachaBatch[]>([]);
    const [selectedNmId, setSelectedNmId] = useState<number | undefined>(undefined);
    const [entries, setEntries] = useState<INachaEntry[]>([]);
    const [headerRecord, setHeaderRecord] = useState<INachaHeaderRecord | undefined>(undefined);
    const [batchRecord, setBatchRecord] = useState<INachaBatchRecord | undefined>(undefined);
    const [bControl, setBControl] = useState<INachaBControlRecord | undefined>(undefined);
    const [fControl, setFControl] = useState<INachaFControlRecord | undefined>(undefined);
    const [listMessage, setListMessage] = useState<string>('');
    const [generateMessage, setGenerateMessage] = useState<string>('');
    const [blankAccounts, setBlankAccounts] = useState<Array<{ tsdNo: number; lName?: string; fName?: string; corpName?: string }>>([]);

    useEffect(() => {
        const fetchLookups = async () => {
            const bankResult = await getLookups('/v1.0/Payroll/nacha/banks');
            if (Array.isArray(bankResult)) {
                setBanks(bankResult.map((b: { code: string; dscr: string }) => ({ value: b.code, label: b.dscr })));
            }
            const compResult = await getLookups('/v1.0/Payroll/nacha/companies');
            if (Array.isArray(compResult)) {
                setCompanies(compResult.map((c: { code: string; dscr: string }) => ({ value: c.code, label: c.dscr })));
            }
        };
        fetchLookups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchBatches = async () => {
        const result = await get('/v1.0/Payroll/nacha/batches');
        if (Array.isArray(result)) {
            setBatches(result);
        }
    };

    const fetchBatchDetail = async (nmId: number) => {
        const entryResult = await get(`/v1.0/Payroll/nacha/batches/${nmId}/entries`);
        if (Array.isArray(entryResult)) {
            setEntries(entryResult);
        }
    };

    const handleOpenBatchList = () => {
        fetchBatches();
        props.showDialog(DialogList.NACHA_BATCH_LIST);
    };

    const handleSelectBatch = (nmId: number) => {
        setSelectedNmId(nmId);
        fetchBatchDetail(nmId);
    };

    const handleOpenGenerate = () => {
        setGenerateMessage('');
        setBlankAccounts([]);
        props.showDialog(DialogList.NACHA_GENERATE);
    };

    // Mirrors DD_Parms' KEY-NEXT-ITEM trigger: once a pay period is entered, default the
    // deposit date to that period's end date + 1 month. Oracle raises an alert if the period
    // doesn't exist; here a missing period is silent (no message, just no auto-fill) since a
    // bad period number is already caught by the required-field validation on submit.
    const handlePeriodBlur = async () => {
        const periodNo = getValues().periodNo;
        if (!periodNo) {
            return;
        }
        const result = await getPeriodDate(`/v1.0/Payroll/nacha/periods/${periodNo}/depositdate`);
        if (result?.depositDate) {
            setValue('depDate', result.depositDate);
        }
    };

    const handleGenerate = async () => {
        const values = getValues();
        setGenerateMessage('');
        setBlankAccounts([]);

        await post('/v1.0/Payroll/nacha/generate', {
            periodNo: Number(values.periodNo),
            nachaType: values.nachaType,
            bankNo: Number(values.bankNo),
            compNo: Number(values.compNo),
            depDate: values.depDate,
            tsdNo: values.tsdNo ? Number(values.tsdNo) : null,
        });

        if (response.status === 400) {
            const blocked = await response.json();
            setBlankAccounts(Array.isArray(blocked) ? blocked : []);
            setGenerateMessage('The following employees need a bank account number on file before this file can be built:');
            return;
        }

        if (!response.ok) {
            setGenerateMessage('Unable to generate the NACHA file. Please try again.');
            return;
        }

        const result = await response.json();
        await fetchBatches();
        setSelectedNmId(result?.nmId);
        if (result?.nmId) {
            fetchBatchDetail(result.nmId);
        }
        props.showDialog(DialogList.NACHA_BATCH_LIST);
    };

    const handleOpenHeaderBatch = async (nmId: number) => {
        const header = await get(`/v1.0/Payroll/nacha/batches/${nmId}/header`);
        setHeaderRecord(header);
        const batch = await get(`/v1.0/Payroll/nacha/batches/${nmId}/batchrecord`);
        setBatchRecord(batch);
        props.showDialog(DialogList.NACHA_HEADER_BATCH);
    };

    const handleOpenControlData = async (nmId: number) => {
        const bc = await get(`/v1.0/Payroll/nacha/batches/${nmId}/bcontrol`);
        setBControl(bc);
        const fc = await get(`/v1.0/Payroll/nacha/batches/${nmId}/fcontrol`);
        setFControl(fc);
        props.showDialog(DialogList.NACHA_CONTROL_DATA);
    };

    const handleDownloadFile = async (nmId: number) => {
        await get(`/v1.0/Payroll/nacha/batches/${nmId}/file`);
        if (!response.ok) {
            setListMessage('Unable to download the NACHA file. Please try again.');
            return;
        }
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `Nacha_${nmId}.txt`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
    };

    const handleDeleteBatch = async (nmId: number) => {
        if (!window.confirm('Delete this NACHA batch? This cannot be undone.')) {
            return;
        }
        await del(`/v1.0/Payroll/nacha/batches/${nmId}`);
        if (response.ok) {
            if (selectedNmId === nmId) {
                setSelectedNmId(undefined);
                setEntries([]);
            }
            fetchBatches();
        } else {
            setListMessage('Unable to delete the batch. Please try again.');
        }
    };

    return (
        <>
            <Dialog title="Direct Deposit" dialogName={DialogList.NACHA_BATCH_LIST} isExitButtonInvisible={true} bodyStyle={{ width: '65%' }}>
                <table style={{ width: '100%' }}>
                    <thead style={{ textAlign: 'center' }}>
                        <tr>
                            <th>Pay Period</th>
                            <th>Create Date</th>
                            <th>Deposit Date</th>
                            <th>File Type</th>
                            <th>No.</th>
                            <th style={{ width: '7rem' }} />
                            <th style={{ width: '7rem' }} />
                            <th style={{ width: '4rem' }} />
                        </tr>
                    </thead>
                    <tbody>
                        {batches.map((b) => (
                            <tr
                                key={b.nmId}
                                className="table-item-hover"
                                style={{ textAlign: 'center', cursor: 'pointer', backgroundColor: b.nmId === selectedNmId ? '#FFFF66' : '' }}
                                onClick={() => handleSelectBatch(b.nmId)}
                            >
                                <td>{b.periodNo}</td>
                                <td>{new Date(b.createDate).toLocaleDateString()}</td>
                                <td>{new Date(b.depDate).toLocaleDateString()}</td>
                                <td>{NACHA_TYPE_OPTIONS.find((o) => o.value === b.nachaType)?.label ?? b.nachaType}</td>
                                <td>{b.nmId}</td>
                                <td><Button className="blue-hover-button" style={{ width: 'auto' }} text="Header/Batch" onClick={(e) => { e.stopPropagation(); handleOpenHeaderBatch(b.nmId); }} /></td>
                                <td>
                                    <Button
                                        className="blue-hover-button"
                                        style={{ width: 'auto', whiteSpace: 'nowrap' }}
                                        text="Control Data"
                                        onClick={(e) => { e.stopPropagation(); handleOpenControlData(b.nmId); }}
                                    />
                                </td>
                                <td><Button className="blue-hover-button" style={{ width: 'auto' }} text="Delete" onClick={(e) => { e.stopPropagation(); handleDeleteBatch(b.nmId); }} /></td>
                            </tr>
                        ))}
                        {batches.length === 0 && (
                            <tr><td colSpan={8} className="bold-centered">No NACHA batches found</td></tr>
                        )}
                    </tbody>
                </table>

                <table style={{ width: '100%', marginTop: '1rem' }}>
                    <thead style={{ textAlign: 'center' }}>
                        <tr>
                            <th>Type</th>
                            <th>DLC No.</th>
                            <th>Payee Name</th>
                            <th>Trans. Amount</th>
                            <th>Routing Number</th>
                            <th>Bank Account No.</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((e) => (
                            <tr key={e.ndId} style={{ textAlign: 'center' }}>
                                <td>{e.tranCode}</td>
                                <td>{e.tsdNo}</td>
                                <td>{e.tsdName}</td>
                                <td>{e.tranAmt?.toFixed(2)}</td>
                                <td>{e.routing}</td>
                                <td>{e.bankAcct}</td>
                            </tr>
                        ))}
                        {entries.length === 0 && (
                            <tr><td colSpan={6} className="bold-centered">Select a batch above to see its entries</td></tr>
                        )}
                    </tbody>
                </table>
                <div style={{ marginTop: '1rem', minHeight: '1.2rem', textAlign: 'center', color: 'red' }}>{listMessage}</div>
                <div className="form-row" style={{ justifyContent: 'center', marginTop: '1rem' }}>
                    <Button className="blue-hover-button" style={{ width: 'auto', marginRight: '1rem' }} text="Generate" onClick={handleOpenGenerate} />
                    <Button
                        className="blue-hover-button"
                        style={{ width: 'auto', marginRight: '1rem' }}
                        text="Download File"
                        disabled={selectedNmId === undefined}
                        onClick={() => selectedNmId !== undefined && handleDownloadFile(selectedNmId)}
                    />
                    <Button className="blue-hover-button" style={{ width: 'auto' }} text="Exit" onClick={() => props.hideDialog()} />
                </div>
            </Dialog>

            <Dialog title="Generate Direct Deposit" dialogName={DialogList.NACHA_GENERATE} isExitButtonInvisible={true} bodyStyle={{ width: '30%' }}>
                <div className="form-row">
                    <RHFInputBox
                        label="Pay Period"
                        name="periodNo"
                        type="number"
                        register={register}
                        labelOnLeft={true}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '50%' }}
                        errors={errors}
                        isRequired={true}
                        rules={{ required: 'Pay Period is required' }}
                        onBlur={handlePeriodBlur}
                    />
                    <RHFInputBox
                        label="TSDI No."
                        title="Blank for All"
                        name="tsdNo"
                        type="number"
                        register={register}
                        labelOnLeft={true}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '50%' }}
                    />
                </div>
                <RHFSelect
                    label="Nacha File Type"
                    name="nachaType"
                    options={NACHA_TYPE_OPTIONS}
                    defaultVal="A"
                    register={register}
                    labelOnLeft={true}
                    style={{ width: '100%' }}
                />
                <RHFSelect
                    label="Bank Name"
                    name="bankNo"
                    options={banks}
                    register={register}
                    labelOnLeft={true}
                    style={{ width: '100%' }}
                    errors={errors}
                    isRequired={true}
                    rules={{ required: 'Bank Name is required' }}
                />
                <RHFSelect
                    label="Company Name"
                    name="compNo"
                    options={companies}
                    register={register}
                    labelOnLeft={true}
                    style={{ width: '100%' }}
                    errors={errors}
                    isRequired={true}
                    rules={{ required: 'Company Name is required' }}
                />
                <RHFInputBox
                    label="Deposit Date"
                    name="depDate"
                    type="date"
                    register={register}
                    labelOnLeft={true}
                    style={{ width: '100%' }}
                    errors={errors}
                    isRequired={true}
                    rules={{ required: 'Deposit Date is required' }}
                />
                <div className="form-row" style={{ marginTop: '1.5rem', justifyContent: 'center' }}>
                    <Button className="blue-hover-button" style={{ width: 'auto', marginRight: '1rem' }} text="OK" onClick={handleSubmit(handleGenerate)} />
                    <Button className="blue-hover-button" style={{ width: 'auto' }} text="Cancel" onClick={handleOpenBatchList} />
                </div>
                {generateMessage && (
                    <div style={{ marginTop: '1rem', color: 'red' }}>
                        <div>{generateMessage}</div>
                        <ul>
                            {blankAccounts.map((a) => (
                                <li key={a.tsdNo}>{a.tsdNo} - {a.corpName || `${a.fName ?? ''} ${a.lName ?? ''}`.trim()}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </Dialog>

            <Dialog title="Header / Batch Record" dialogName={DialogList.NACHA_HEADER_BATCH} isExitButtonInvisible={true} bodyStyle={{ width: '45%' }}>
                <div className="form-row">
                    <div style={{ width: '50%', paddingRight: '1rem' }}>
                        <SectionHeading text="Header Record" />
                        <FieldRow label="Priority Code" value={headerRecord?.priorityCode} />
                        <FieldRow label="Immediate Destination" value={headerRecord?.imDestination} />
                        <FieldRow label="Immediate ID" value={headerRecord?.imId} />
                        <FieldRow label="Create Date" value={headerRecord?.createDate} />
                        <FieldRow label="Create Time" value={headerRecord?.createTime} />
                        <FieldRow label="File ID Modifier" value={headerRecord?.fileId} />
                        <FieldRow label="Record Size" value={headerRecord?.recordSize} />
                        <FieldRow label="Blocking Factor" value={headerRecord?.blockFactor} />
                        <FieldRow label="Format Code" value={headerRecord?.formatCode} />
                        <FieldRow label="Destination Name" value={headerRecord?.destName} />
                        <FieldRow label="Origin Name" value={headerRecord?.originName} />
                        <FieldRow label="Reference Code" value={headerRecord?.refCode} />
                    </div>
                    <div style={{ width: '50%', paddingLeft: '1rem' }}>
                        <SectionHeading text="Batch Record" />
                        <FieldRow label="Class Code" value={batchRecord?.classCode} />
                        <FieldRow label="Company" value={batchRecord?.company} />
                        <FieldRow label="Company Data" value={batchRecord?.companyData} />
                        <FieldRow label="Company ID" value={batchRecord?.imId} />
                        <FieldRow label="Entry Class Code" value={batchRecord?.entryClass} />
                        <FieldRow label="Entry Description" value={batchRecord?.entryDesc} />
                        <FieldRow label="Description Date" value={batchRecord?.descDate} />
                        <FieldRow label="Effective Date" value={batchRecord?.effDate} />
                        <FieldRow label="Settlement Date" value={batchRecord?.settleDate} />
                        <FieldRow label="Status Code" value={batchRecord?.statusCode} />
                        <FieldRow label="Origin ID" value={batchRecord?.dfiId} />
                        <FieldRow label="Batch No." value={batchRecord?.batchNo} />
                    </div>
                </div>
                <div className="form-row" style={{ marginTop: '1.5rem', justifyContent: 'center' }}>
                    <Button className="blue-hover-button" style={{ width: 'auto' }} text="OK" onClick={handleOpenBatchList} />
                </div>
            </Dialog>

            <Dialog title="Control Data" dialogName={DialogList.NACHA_CONTROL_DATA} isExitButtonInvisible={true} bodyStyle={{ width: '45%' }}>
                <div className="form-row">
                    <div style={{ width: '50%', paddingRight: '1rem' }}>
                        <SectionHeading text="Batch Control Record" />
                        <FieldRow label="Service Code" value={bControl?.serviceCode} />
                        <FieldRow label="Addenda Indicator" value={bControl?.addenda} />
                        <FieldRow label="Entry Hash No." value={bControl?.entryHash} />
                        <FieldRow label="Entry Debit Total" value={bControl?.debit} />
                        <FieldRow label="Entry Credit Total" value={bControl?.credit} />
                        <FieldRow label="Comp ID" value={bControl?.compId} />
                        <FieldRow label="Originating ID" value={bControl?.dfiId} />
                        <FieldRow label="Trace No." value={bControl?.traceNo} />
                    </div>
                    <div style={{ width: '50%', paddingLeft: '1rem' }}>
                        <SectionHeading text="File Control Record" />
                        <FieldRow label="Batch Count" value={fControl?.batchCount} />
                        <FieldRow label="Block Count" value={fControl?.blockCount} />
                        <FieldRow label="Entry Count" value={fControl?.entryCount} />
                        <FieldRow label="Entry Hash No." value={fControl?.entryHash} />
                        <FieldRow label="Entry Debit Total" value={fControl?.debit} />
                        <FieldRow label="Entry Credit Total" value={fControl?.credit} />
                    </div>
                </div>
                <div className="form-row" style={{ marginTop: '1.5rem', justifyContent: 'center' }}>
                    <Button className="blue-hover-button" style={{ width: 'auto' }} text="OK" onClick={handleOpenBatchList} />
                </div>
            </Dialog>
        </>
    );
};

export default NachaDialogs;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/checkRegister/NachaDialogs.tsx